import { useState, useRef, useEffect, useCallback } from 'react';

export function useSpeech() {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const stop = useCallback(() => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, []);

  const getBestVoice = useCallback(() => {
    const voices = window.speechSynthesis.getVoices();
    
    // Priority list for Indian Female husky-compatible voices
    const priorities = [
      (v: SpeechSynthesisVoice) => v.name.toLowerCase().includes('heera'),
      (v: SpeechSynthesisVoice) => v.name.toLowerCase().includes('veena'),
      (v: SpeechSynthesisVoice) => v.name.toLowerCase().includes('google india female'),
      (v: SpeechSynthesisVoice) => v.lang.includes('en-IN') && (v.name.toLowerCase().includes('female') || ['neerja', 'priya', 'sangeeta'].some(name => v.name.toLowerCase().includes(name))),
      (v: SpeechSynthesisVoice) => v.lang.includes('en-IN'),
      (v: SpeechSynthesisVoice) => v.name.toLowerCase().includes('female') && v.lang.startsWith('en'),
    ];

    for (const priority of priorities) {
      const voice = voices.find(priority);
      if (voice) return voice;
    }

    return voices.find(v => v.lang.startsWith('en')) || voices[0];
  }, []);

  const unlock = useCallback(() => {
    // Some browsers (iOS) need resume() and a silent utterance within a user interaction
    window.speechSynthesis.resume();
    const silent = new SpeechSynthesisUtterance('');
    silent.volume = 0;
    window.speechSynthesis.speak(silent);
  }, []);

  const speak = useCallback((text: string) => {
    if (!text) return;

    stop();

    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;

    // Emulate Indian female husky voice
    utterance.pitch = 0.8;
    utterance.rate = 0.9;
    
    const voice = getBestVoice();
    if (voice) {
      utterance.voice = voice;
    }

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      utteranceRef.current = null;
    };
    utterance.onerror = () => {
      setIsSpeaking(false);
      utteranceRef.current = null;
    };

    // Stability fix for mobile: small delay before speaking
    setTimeout(() => {
      window.speechSynthesis.speak(utterance);
    }, 100);
  }, [stop, getBestVoice]);

  useEffect(() => {
    // Pre-load voices
    window.speechSynthesis.getVoices();
    const handleVoicesChanged = () => window.speechSynthesis.getVoices();
    window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);
    
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
      window.speechSynthesis.cancel();
    };
  }, []);

  return {
    isSpeaking,
    speak,
    stop,
    unlock
  };
}
